import { IPoint } from './GeographyTypes'

export class Bearing {
  public readonly angleDegrees: number

  private constructor(angle: number) {
    this.angleDegrees = Bearing.normalize(angle)
  }

  /**
   * Creates a new Bearing from an angle in degrees, ranging -180 to 180.
   *  - Positive values represent clockwise/right turns
   *  - Negative values represent counterclockwise/left turns
   * @param angle - The angle in degrees
   * @returns A new Bearing instance
   */
  public static fromAngleDegrees(angle: number): Bearing {
    return new Bearing(angle)
  }

  /**
   * Creates a new Bearing from two points, where 0 is north, 90 is east,
   * 180 is south, and -90 is west.
   * @param from - The starting point
   * @param to - The ending point
   * @returns A new Bearing instance
   */
  public static fromPoints(from: IPoint, to: IPoint): Bearing {
    const [fromX, fromY] = from.coordinates
    const [toX, toY] = to.coordinates

    // Calculate angle using atan2, which gives angle from east with CCW positive
    const rawAngle = Math.atan2(toY - fromY, toX - fromX) * (180 / Math.PI)

    // Convert to angle from north with CW positive
    // 1. Subtract 90 to make north 0 degrees
    // 2. Negate the result to change direction (CCW to CW)
    const northBasedAngle = -1 * (rawAngle - 90)

    return new Bearing(northBasedAngle)
  }

  /**
   * Calculates the relative bearing from one bearing to another, as if the "from" bearing were
   * true north.
   */
  public static getRelativeBearing(from: Bearing, to: Bearing): Bearing {
    return new Bearing(to.angleDegrees - from.angleDegrees)
  }

  /**
   * Normalizes a bearing to be within the range -180 to 180
   * @param bearing - The bearing to normalize
   * @returns The normalized bearing in the range -180 to 180
   */
  private static normalize(bearing: number): number {
    // Use modulo to get the equivalent angle in the -360 to 360 range
    let result = bearing % 360

    // Adjust to the -180 to 180 range
    if (result > 180) {
      result -= 360
    } else if (result <= -180) {
      result += 360
    }

    return result
  }
}
